import { gqlFetcher, graphql } from '#features/api';
import type { ResultOf } from '#features/api/utils/graphql';

const myCallPolicyDoc = graphql(`
  query MyCallPolicy {
    myCallPolicy {
      directCalls
      invitations
    }
  }
`);

const updateCallPolicyDoc = graphql(`
  mutation UpdateCallPolicy($input: CallPolicyInput!) {
    updateCallPolicy(input: $input) {
      directCalls
      invitations
    }
  }
`);

const callReachDoc = graphql(`
  query CallReach($peerUserId: ID!) {
    callReach(peerUserId: $peerUserId) {
      action
    }
  }
`);

export type CallPolicy = ResultOf<typeof myCallPolicyDoc>['myCallPolicy'];
export type CallDirectPolicy = CallPolicy['directCalls'];
export type CallInvitationPolicy = CallPolicy['invitations'];
export type CallReachAction = ResultOf<typeof callReachDoc>['callReach']['action'];

export const CALL_DIRECT_POLICIES = ['FRIENDS', 'OFF'] as const satisfies CallDirectPolicy[];
export const CALL_INVITATION_POLICIES = [
  'ALL',
  'FRIENDS',
  'OFF',
] as const satisfies CallInvitationPolicy[];

/** Умолчания сервера: строки в таблице нет, пока человек ничего не менял. */
export const DEFAULT_CALL_POLICY: CallPolicy = { directCalls: 'FRIENDS', invitations: 'ALL' };

export const CALL_POLICY_QUERY_KEY = ['call-policy'] as const;

export const callPolicyQueryOptions = () => ({
  queryKey: CALL_POLICY_QUERY_KEY,
  queryFn: () => gqlFetcher(myCallPolicyDoc).then((data) => data.myCallPolicy),
});

export const updateCallPolicy = (input: CallPolicy): Promise<CallPolicy> =>
  gqlFetcher(updateCallPolicyDoc, { input }).then((data) => data.updateCallPolicy);

/** Общий корень ключей допуска: подписка, отписка и блок меняют дружбу, а с ней и ответ. */
export const CALL_REACH_QUERY_KEY = ['call-reach'] as const;

export const callReachQueryKey = (peerUserId: string) =>
  [...CALL_REACH_QUERY_KEY, peerUserId] as const;

export const fetchCallReach = (peerUserId: string): Promise<CallReachAction> =>
  gqlFetcher(callReachDoc, { peerUserId }).then((data) => data.callReach.action);

/**
 * Ответ «что можно сделать с этим человеком» живёт недолго: собеседник может поменять настройки
 * или перестать быть другом в любую минуту. Сервер всё равно перепроверяет при наборе, поэтому
 * минута устаревания стоит не больше, чем лишний отказ с понятным текстом.
 */
export const callReachQueryOptions = (peerUserId: string) => ({
  queryKey: callReachQueryKey(peerUserId),
  queryFn: () => fetchCallReach(peerUserId),
  staleTime: 60_000,
});
